import React, { useCallback, useMemo, useState } from 'react';
import { Divider, VStack, Flex, Box } from '@chakra-ui/react';
import { HardFacts } from './HardFacts';
import { PriceAndCta } from './PriceAndCta';
import { ShortDescription } from './ShortDescription';
import { Location } from './Location';
import { ApartmentTitle } from './ApartmentTitle';
import { Bookmark } from './Bookmark';
import { PreviewSlider } from './PreviewSlider';
import { SingleObject } from '../../../types/ObjectTypes';
import { Link } from 'react-router-dom';
import { ObjectParams, ObjectsParams } from '../../../types/ParamsTypes';
import {
  getExtendedObjectRoute,
  getSimpleObjectRoute,
  isQualifiedToFetchWithExtendedParams,
} from '../../../services/Object/ObjectService';
import { ExtendedDetailsRoute } from '../../../types/RoutesTypes';
import { APP_VIEW, getAppView } from '../../../services/VersionManagement/VersionManagementService';

export interface ListItemProps {
  object: SingleObject;
  objectsParams: ObjectsParams | ObjectParams;
  bookmarks: Array<ExtendedDetailsRoute>;
  handleBookmarkChange: Function;
}

export const ListItem = (props: ListItemProps): JSX.Element => {
  const { object, objectsParams, bookmarks, handleBookmarkChange } = props;
  const { baseData, imagesData, textsData } = object;
  const [appView] = useState<APP_VIEW>(getAppView());

  const bookmarkParams = useMemo((): ExtendedDetailsRoute => {
    const params = {} as ExtendedDetailsRoute;
    params.id = object.baseData.uniqueId;
    params.numberOfAdults = objectsParams.numberOfAdults.toString();
    params.numberOfChildren = objectsParams.numberOfChildren.toString();
    params.toDate = objectsParams.toDate;
    params.fromDate = objectsParams.fromDate;
    params.agesOfChildren = objectsParams.agesOfChildren.join(',');
    params.numberOfPets = objectsParams.numberOfPets.toString();
    return params;
  }, [object, objectsParams]);

  return (
    <Flex
      border={'1px'}
      borderColor={'gray.200'}
      borderRadius={'8'}
      w={'100%'}
      _hover={{ boxShadow: 'lg' }}
      height={{ xl: 300 }}
      bg={'white'}
      flexDirection={{ base: 'column', xl: 'row', md: appView === APP_VIEW.MIN ? "row" : "column" }}>
      <PreviewSlider images={imagesData} object={object} objectsParams={objectsParams} />
      <VStack
        alignItems={'flex-stretch'}
        w={{ base: '100%', xl: '50%' }}
        pl={4}
        pr={4}
        pt={2}
        pb={2}>
        <Flex alignItems={'baseline'} h={8}>
          <Link
            to={{
              pathname: isQualifiedToFetchWithExtendedParams({
                id: object.baseData.uniqueId,
                fromDate: objectsParams.fromDate,
                toDate: objectsParams.toDate,
              })
                ? getExtendedObjectRoute(object, objectsParams)
                : getSimpleObjectRoute(object, objectsParams),
            }}
            target='_blank'
            style={{ width: '100%' }}>
            <ApartmentTitle title={baseData.Name} hashRoute={'details'} />
          </Link>
          <Box alignSelf={'center'}>
            {appView !== APP_VIEW.MIN &&
              <Bookmark
                size={'2xl'}
                objectUniqueId={object.baseData.uniqueId}
                bookmarks={bookmarks}
                handleBookmarkChange={handleBookmarkChange}
                bookmarkParams={bookmarkParams}
              />
            }
          </Box>
        </Flex>
        <Box h={8}>
          <Location location={baseData.Region + ' / ' + baseData.City} />
        </Box>

        <Divider />
        <HardFacts
          maximumPersons={baseData.maximumPersons}
          sizeQm={baseData.sizeQm}
          numberOfBedrooms={baseData.numberOfBedrooms}
          numberOfPets={baseData.numberOfPets}
        />
        <Divider />
        <Box h={'100%'}>
          <ShortDescription textsData={textsData} />
        </Box>
        <Box mt={0} flex={1}>
          <PriceAndCta object={object} objectsParams={objectsParams} />
        </Box>
      </VStack>
    </Flex>
  );
};
